/**
 * useMyTasks — list cleaning_tasks assigned to the current user.
 *
 * RLS does the security work: even if the query had no `eq` filter, the
 * cleaner-scoped policy would limit rows to their own. We pass the filter
 * anyway to keep the query plan tight on busy orgs.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useAuthStore }                   from '@/store/authStore'

export interface MyTaskRow {
  id:           string
  room_id:      string
  status:       string
  cycle_type:   string
  started_at:   string | null
  completed_at: string | null
  room: {
    id:             string
    room_number:    string
    current_status: string
  } | null
}

export function useMyTasks(): UseQueryResult<MyTaskRow[]> {
  const supabase = useSupabase()
  const userId   = useAuthStore((s) => s.user?.id ?? null)

  return useQuery({
    queryKey: ['my-tasks', userId],
    enabled:  !!userId,
    queryFn: async (): Promise<MyTaskRow[]> => {
      if (!userId) return []
      const { data, error } = await supabase
        .from('cleaning_tasks')
        .select('id, room_id, status, cycle_type, started_at, completed_at, room:rooms(id, room_number, current_status)')
        .eq('assigned_to_user_id', userId)
        .order('started_at', { ascending: false, nullsFirst: false })
        .limit(50)

      if (error) throw error
      return (data ?? []) as unknown as MyTaskRow[]
    },
  })
}
